import { createMuiTheme } from '@material-ui/core/styles';
import { getMuiButtonOverrides } from './muiButtonOverrides';

/**
 * Single source of truth for the app theme.
 *
 * Everything that is not brand data (semantic colors, shape, and in time
 * typography and spacing) lives here as a base layer. The brand fetched from
 * the API only overrides what a brand is allowed to own: primary, secondary,
 * text color, logos and name. Keeping the split explicit avoids the drift that
 * comes from maintaining a second, nested theme somewhere else in the tree.
 */
export function createAppTheme(brand) {
  const baseMuiTheme = createMuiTheme({
    shape: {
      borderRadius: 8,
      // The corner of a dialog's paper.
      dialogRadius: 16,
      // The corner of a control the operator presses or types in, in the
      // documents section's toolbar and dialogs.
      controlRadius: 10,
      // The square tile that carries a confirmation dialog's icon.
      iconTileRadius: 14,
      pillRadius: 999,
    },
    typography: {
      // A dialog's title. MUI's default scale has no step for it: h6 is larger
      // at 1.25rem and lighter at 500.
      dialogTitle: {
        fontSize: '1.125rem',
        fontWeight: 700,
      },
    },
    palette: {
      // --- brand-driven ---
      primary: {
        main: brand?.primaryColor || '#e17414',
      },
      secondary: {
        main: brand?.secondaryColor || '#ff0000',
        // Neutral gray for disabled/secondary affordances (ActionButton,
        // GoToButton, CustomHeader). Not brand-driven: it must stay readable
        // whatever the brand picks.
        other: '#adadad',
      },
      text: {
        main: brand?.textColor || '#ffffff',
      },

      // --- semantic, never brand-driven ---
      warning: {
        // Superficie, non testo: su bianco questo giallo sta a 1.1:1 di
        // contrasto. Si usa come sfondo (CustomSnackbar, CustomTooltip), con
        // sopra `contrastText`.
        main: '#fef8a0',
        // The stronger step of the same warning, also a surface: the amber that
        // sets «expires within 15 days» apart from the 30-day yellow in the
        // documents' Scadenza column. Explicit because MUI would derive it by
        // darkening the yellow into a dull olive. Carries the same
        // `contrastText` (7.3:1).
        dark: '#FFCC80',
        // Esplicito: quello che MUI deriverebbe da solo e' rgba(0,0,0,0.87),
        // mentre la coppia scelta per questo giallo e' questo bruno (10.6:1
        // sul giallo).
        contrastText: '#4e3b00',
      },
      error: {
        main: '#D72A2A', //red
      },
      success: {
        main: '#4caf50', //green
      },
      disabled: {
        main: '#e0e0e0', //light gray
      },
      statusBadge: {
        success: { bg: '#DCFCE7', border: '#86EFAC', text: '#15803D' },
        warning: { bg: '#FEF3C7', border: '#FCD34D', text: '#B45309' },
        info: { bg: '#DBEAFE', border: '#93C5FD', text: '#1D4ED8' },
        neutral: { bg: '#F0EFEE', border: '#D4D4D4', text: '#383838' },
      },
      // The fill of a placeholder standing in for content that is still
      // loading: the shapes of a skeleton.
      placeholder: {
        main: '#eeeeee',
      },
      // A surface that carries no judgement, for an outcome that is neither
      // good nor bad: the tile behind the icon of a check that could not run.
      neutral: {
        light: '#f5f5f5',
      },
    },
    logo: brand?.logo || '',
    logoLeft: brand?.logoLeft || '',
    nomeBrand: brand?.nomeBrand || 'BS-FOOD',
    path: brand?.path || 'bsfood',
  });

  // Button overrides read the palette, so they are built from the theme above
  // rather than from a fixed palette.
  return createMuiTheme(baseMuiTheme, {
    overrides: {
      MuiButton: getMuiButtonOverrides(baseMuiTheme),
    },
  });
}
